import { Action, Module } from "vuex-class-modules"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { store } from "../store"
import { Modules } from "../modules"
import { BaseModule } from "@/models/BaseModule"
import { accountModule } from "@/store/modules/account.module"
import {
  SearchResult,
  SearchPayload,
  SearchIdeaResult,
  SearchProjectResult,
  SearchSprintResult,
  SearchMilestoneResult,
} from "@/models/Search"
import { Mutation } from "vuex-class-modules"
import { Status } from "@/models/Status"
import { typeCreateById } from "@/models/Type"
import { ServerResponse } from "@/models/ServerResponse"
import {
  Filter,
  HttpError,
  Item,
  Project,
  Task,
  Team,
  FilterSearchItem,
  FilterSearch,
  LabelSearchFilter,
  FilterTeam,
  SearchProject,
  FilterEpic,
  FilterIdea,
  Epic,
  MilestoneSearch,
} from "@/models"
import { ProjectActions, projectModule } from "./project.module"
import { FilterSprint } from "@/models/Filter/FilterSprint"
import { parsingServerResponseContent } from "@/utils/helpers"

export enum SearchActions {
  SEARCH_RESOURCE = "SEARCH_RESOURCE",
  SEARCH_ITEM = "SEARCH_ITEM",
  SEARCH_PROJECT = "SEARCH_PROJECT",
  SEARCH_SPRINT = "SEARCH_SPRINT",
  SEARCH_EPIC = "SEARCH_EPIC",
  SEARCH_IDEA = "SEARCH_IDEA",
  SEARCH_TEAM = "SEARCH_TEAM",
  SEARCH_MILESTONE = "SEARCH_MILESTONE",
  SEARCH_ITEM_WITH_TASKS = "SEARCH_ITEM_WITH_TASKS",
  SEARCH_LABELS = "SEARCH_LABELS",
  RESET_SEARCH = "RESET_SEARCH",
  SEARCH_PROJECT_BY_FILTER = "SEARCH_PROJECT_BY_FILTER",
}

const FILTER_OPTIONS = {
  page: 0,
  size: 1000,
  sort: [],
}

@Module
class SearchModule extends BaseModule {
  searchResult: SearchResult[]
  searchSprintResult: SearchSprintResult[]
  searchProjectResult: SearchProjectResult[] = []
  
  get projectId() {
    return projectModule.currentProjectId
  }

  get accountId() {
    return accountModule.accountId
  }

  get [`get/${SearchActions.SEARCH_RESOURCE}`]() {
    return this.searchResult
  }

  get [`get/${SearchActions.SEARCH_SPRINT}`]() {
    return this.searchSprintResult
  }

  get [`get/${SearchActions.SEARCH_PROJECT}`]() {
    return this.searchProjectResult
  }

  @Mutation
  setResult(result: SearchResult[]) {
    this.searchResult = result
  }

  @Mutation
  setProjectResult(result: SearchProjectResult[]) {
    this.searchProjectResult = result
  }

  @Mutation
  setSearchSprintResult(result: ServerResponse<SearchSprintResult>) {
    this.searchSprintResult = parsingServerResponseContent({
      originalContent: this.searchSprintResult,
      serverResponseContent: result.content,
      numberOfPage: result.pageable.pageNumber,
    })
  }

  @Action
  async [SearchActions.SEARCH_RESOURCE](payload: SearchPayload) {
    const data = await apiService.get<SearchResult[]>(Endpoint.SEARCH(this.accountId), payload)
    return this.handleResponse<SearchResult[]>(data, (data) => {
      this.setResult(data)
      return data
    })
  }

  /** Searches ToDo labels across the account and keeps existing labels project-scoped. */
  @Action
  async [SearchActions.SEARCH_LABELS](payload: LabelSearchFilter) {
    const params = payload.getJsonObj()
    params.append("projectId", this.projectId)
    const data = await apiService.get<ServerResponse<string>>(Endpoint.SEARCH_LABELS(this.accountId), params)
    return this.handleResponse<ServerResponse<string>>(data, (data) => {
      return data
    })
  }

  @Action
  async [SearchActions.SEARCH_ITEM](filter: FilterSearchItem) {
    if (filter.projectName && !filter.projectIds?.length) {
      const projectId = await apiService.get<string>(Endpoint.PROJECT_RESOLVE(this.accountId), {
        name: filter.projectName,
      })
      if (projectId && !(projectId instanceof HttpError)) {
        filter.projectIds = [projectId]
      }
    }
    const data = await apiService.get<ServerResponse<Item>>(Endpoint.SEARCH_ITEM(this.accountId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<Item>>(data, (data) => {
      data.content =  data.content.map((result) => Item.create(result))
      this.setResult(data.content  as unknown as SearchResult[])
      return data
    }, true)
  }

  @Action
  async [SearchActions.SEARCH_ITEM_WITH_TASKS](payload: { sequenceNumber: string, projectIds: string[] } = {
    sequenceNumber: "",
    projectIds: [],
  }) {
    const itemResponse = await this[SearchActions.SEARCH_ITEM](new FilterSearchItem({
      q: payload.sequenceNumber,
      projectIds: payload.projectIds
    }))
    const result = itemResponse.content
    const item = result.find(result => result.sequenceNumber.toString() === payload.sequenceNumber)
    if (item) {
      const data = await apiService.get<Item>(Endpoint.SEARCH_ITEM_WITH_TASKS(item.projectId, item.id))
      return this.handleResponse<Item>(data, (data) => {
        const itemType = data.itemType
        const status = data.status
        const itemTypeParsed = typeCreateById({ id: itemType })
        return {
          id: data.id,
          title: data.title,
          itemType,
          itemTypeParsed,
          status,
          statusParsed: Status.createById({ id: status }),
          sequenceNumber: data.sequenceNumber,
          tasks: data.tasks.map(task => new Task(task)).sort((a, b) => a.order - b.order),
        }
      }, true)
    } else {
      return this.handleResponse<boolean>(false)
    }
  }

  @Action
  async [SearchActions.SEARCH_PROJECT](q: string) {
    const filter = FilterSearch.create({
      q,
    })

    filter.addSort("name_keyword", true)
    const data = await apiService.get<ServerResponse<SearchProjectResult>>(Endpoint.SEARCH_PROJECT(this.accountId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<SearchProjectResult>>(data, (data) => {
      const items = data.content.map(result => ({
        id: result.id,
        name: result.name,
        urlName: result.urlName,
      }))

      this.setResult(items as unknown as SearchResult[])
      return data
    }, true)
  }

  @Action
  async [SearchActions.SEARCH_PROJECT_BY_FILTER](filter: SearchProject) {
    const data = await apiService.get<ServerResponse<SearchProjectResult>>(Endpoint.SEARCH_PROJECT(this.accountId), new SearchProject(filter).getJsonObj())
    return this.handleResponse<ServerResponse<SearchProjectResult>>(data, (data) => {
      data.content = data.content.map(result => (new SearchProjectResult(result)))
      this.setProjectResult(data.content)
      return data
    }, true)
  }

  @Action
  async [SearchActions.SEARCH_SPRINT]({ filter, onlyGet } : { filter: FilterSprint, onlyGet?: boolean }) {
    let team
    if (!filter.teamIds?.length && filter.teamName) {
      const data = await apiService.get<string>(Endpoint.TEAM_RESOLVE(this.accountId), {
        name: filter.teamName,
      })
      if (!(data instanceof HttpError)) {
        filter.teamIds = [data]
        team = await apiService.get<Team>(Endpoint.TEAM(this.accountId, filter.teamIds[0]))
      }
    }

    const data = await apiService.get<ServerResponse<SearchSprintResult>>(Endpoint.SEARCH_SPRINT(this.accountId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<SearchSprintResult>>(data, (data) => {
      data.content = data.content.map(result => new SearchSprintResult({
        ...result,
        team,
      }))
      if (!onlyGet) {
        this.setResult(data.content as unknown as SearchResult[])
      }
      return data
    }, true)
  }

  @Action
  async [SearchActions.SEARCH_EPIC](filter: FilterEpic) {
    const { projectName } = filter
    if (!filter.projectIds.length && projectName) {
      const projectId = await apiService.get<string>(Endpoint.PROJECT_RESOLVE(this.accountId), {
        name: projectName,
      })
      if(projectId && !(projectId instanceof HttpError)) {
        filter.projectIds = [projectId]
      }
    }

    const data = await apiService.get<ServerResponse<Epic>>(Endpoint.SEARCH_EPIC(this.accountId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<Epic>>(data, (data) => {
      data.content = data.content.map(result => new Epic(result))
      this.setResult(data.content as unknown as SearchResult[])
      return data
    }, true)
  }

  @Action
  async [SearchActions.SEARCH_IDEA](filter: FilterIdea) {
    if (filter.projectName && !filter.projectIds?.length) {
      const projectId = await apiService.get<string>(Endpoint.PROJECT_RESOLVE(this.accountId), {
        name: filter.projectName,
      })
      if (!projectId || projectId instanceof HttpError) {
        return false
      }
      filter.projectIds = [projectId]
    }

    const data = await apiService.get<ServerResponse<SearchIdeaResult>>(Endpoint.SEARCH_IDEA(this.accountId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<SearchIdeaResult>>(data, (data) => data, true)
  }

  @Action
  async [SearchActions.SEARCH_TEAM](payload: FilterTeam) {
    let projectId = payload.projectId

    if (payload.projectName && !projectId) {
      const data = await apiService.get<string>(Endpoint.PROJECT_RESOLVE(this.accountId), {
        name: payload.projectName,
      })
      if (data && !(data instanceof HttpError)) {
        projectId = data
      }
    }

    const filter = FilterTeam.create({ ...payload, projectId })

    const data = await apiService.get<ServerResponse<Team>>(Endpoint.SEARCH_TEAM(this.accountId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<Team>>(data, (data) => {
      data.content = data.content.map(result => new Team(result))
      this.setResult(data.content as unknown as SearchResult[])
      return data
    }, true)
  }

  @Action
  async [SearchActions.SEARCH_MILESTONE](filter: MilestoneSearch) {
    const { projectName } = filter
    if (projectName && !filter.projectIds?.length) {
      const projectId = await apiService.get<string>(Endpoint.PROJECT_RESOLVE(this.accountId), {
        name: projectName,
      })
      if (projectId && !(projectId instanceof HttpError)) {
        filter.projectIds = [projectId]
      }
    }

    const data = await apiService.get<ServerResponse<SearchMilestoneResult>>(Endpoint.SEARCH_MILESTONE(this.accountId), filter.getJsonObj())
    return this.handleResponse<ServerResponse<SearchMilestoneResult>>(data, (data) => {
      data.content = data.content.map(result => new SearchMilestoneResult(result))
      this.setResult(data.content as unknown as SearchResult[])
      return data
    }, true)
  }

  @Action
  async [SearchActions.RESET_SEARCH]() {
    this.setResult([])
  }
}

export const searchModule = new SearchModule({ store, name: Modules.SEARCH })
